import BaseApi from "./base-api";

/**
 * Class for the Proposal Tool API
 *
 * @extends BaseApi
 * @description This class is used to interact with the propposal tool API endpoint
 */
export class ProposalToolApi extends BaseApi {
  constructor() {
    super("proposal-tool");

    this.methods = {
      GET_INFO: "get_info",
      GET_PROPOSAL: "get_proposal",
      GET_PROPOSAL_BY_PROJECT_ID: "get_proposal_by_project_id",
      GET_MILITARY_MONTHLY: "get_military_monthly",
      GET_SALES_TEAM_TEST: "get_sales_team_test",
      GET_LOAN_PRODUCTS: "get_loan_products",
      GET_ROOF_QUOTES_PER_PROJECT: "get_roof_quotes_per_project",
      GET_ROOF_QUOTE: "get_roof_quote",
      ADD_ROOF_QUOTE: "add_roof_quote",
      DELETE_ROOF_QUOTE: "delete_roof_quote",
      SAVE_PPA_LOAN_PRODUCT: "save_ppa_loan_product",
    };
  }

  savePpaLoanProduct = async (payload) => {
    const response = await this.request({
      method: this.methods.SAVE_PPA_LOAN_PRODUCT,
      ...payload,
    });

    if (response.success) {
      return response;
    }

    throw new Error("Could not save Ppa product to db");
  }

  /**
   * Get Info (Salesforce)
   *
   * @param {Object} data
   * @param {String} data.projectId
   */
  getInfo = async ({ projectId }) => {
    const response = await this.request({
      method: this.methods.GET_INFO,
      project_id: projectId,
    });

    if (response.success) {
      return response;
    }

    throw new Error("Could not get Info");
  };

  /**
   * Get Proposal
   *
   * @param {Object} data
   * @param {String} data.proposalId
   */
  getProposal = async ({ proposalId }) => {
    const response = await this.request({
      method: this.methods.GET_PROPOSAL,
      prop_id: proposalId,
    });

    if (response.success) {
      return response.data;
    }

    throw new Error("Could not get Proposal");
  };

  /**
   * Get Military Monthly
   *
   * @param {Object} data
   * @param {String} data.startDate
   * @param {String} data.endDate
   */
  getMilitaryMonthly = async ({ startDate, endDate }) => {
    const response = await this.request({
      method: this.methods.GET_MILITARY_MONTHLY,
      start: startDate,
      end: endDate,
    });

    if (response.success) {
      return response.data;
    }

    throw new Error("Could not get Military Monthly");
  };

  /**
   * Get Proposals By Project Id
   *
   * @param {Object} data
   * @param {String} data.projectId
   */
  getProposalsByProjectId = async ({ projectId }) => {
    const response = await this.request({
      method: this.methods.GET_PROPOSAL,
      project_id: projectId,
    });

    if (response.success) {
      return response.data;
    }

    throw new Error("Could not get Proposal");
  };

  /**
   * Get Sungage Data
   */
  getSungageData = async ({ sales_team = "" }) => {
    const response = await this.request({
      method: this.methods.GET_SUNGAGE_DATA,
      sales_team,
    });

    if (response.success) {
      return response.data;
    }

    throw new Error("Could not get Sungage Data");
  };

  /**
   * Get the loan products
   */
  getLoanProducts = async () => {
    const response = await this.request({
      method: this.methods.GET_LOAN_PRODUCTS,
    });

    if (response.success) {
      return response.data;
    }

    throw new Error("Could not get Loan Products");
  };

  /**
   * Get all roof quotes for a project
   *
   * @param {Object} data
   * @param {String} data.projectId
   */
  getRoofQuotesPerProject = async ({ projectId }) => {
    const response = await this.request({
      method: this.methods.GET_ROOF_QUOTES_PER_PROJECT,
      project_id: projectId,
    });

    if (response.success) {
      return response.data;
    }

    throw new Error("Could not get Roof Quotes");
  };

  /**
   * Get roof quote
   */
  getRoofQuote = async ({ roofQuoteId }) => {
    const response = await this.request({
      method: this.methods.GET_ROOF_QUOTE,
      quote_id: roofQuoteId,
    });

    if (response.success) {
      return response.roofQuote;
    }

    throw new Error("Could not get Roof Quote");
  };

  /**
   * Add roof quote
   *
   * @param {Object} data
   * @param {String} data.projectId
   * @param {Number} data.roofSqFt
   * @param {Number} data.quoteAmount
   * @param {String} data.roofType
   * @param {Number} data.loanId
   * @param {String} data.sheathing
   * @param {Object} data.adders
   */
  addRoofQuote = async (data) => {
    const response = await this.request({
      ...data.adders,
      method: this.methods.ADD_ROOF_QUOTE,
      project_id: data.projectId,
      quote_amount: data.quoteAmount,
      loan_id: data.loanId,
      pricingTier: data.pricingTier,
    });

    if (response.success) {
      return response.quoteId;
    }

    throw new Error("Could not add Roof Quote");
  };

  /**
   * Delete roof quote
   *
   * @param {String} data.quoteId
   */
  deleteRoofQuote = async ({ quoteId }) => {
    const response = await this.request({
      method: this.methods.DELETE_ROOF_QUOTE,
      quote_id: quoteId,
    });

    if (response.success) {
      return response;
    }

    throw new Error("Could not delete Roof Quote");
  };
}

export default new ProposalToolApi();
